/**
 * ListFieldRenderer — Renders array fields (parties, associations, links) as a list
 * with add/remove UI and per-item editing.
 *
 * Supports:
 * - Display existing items from involved_parties array
 * - Add new items with default values
 * - Remove items with delete button
 * - Edit individual item fields inline
 */

import React, { useRef } from 'react';
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';

import { formatCurrency, formatPercent } from '../utils/format';
import FieldRenderer from './FieldRenderer';

const useStyles = makeStyles((theme) => ({
  listContainer: {
    marginBottom: theme.spacing(2),
  },
  listHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  headerTitle: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  addButton: {
    textTransform: 'none',
    fontSize: '0.875rem',
  },
  emptyState: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  listItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(1),
    marginBottom: theme.spacing(1),
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  itemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  itemTitle: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  itemFields: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  deleteButton: {
    padding: theme.spacing(0.5),
  },
}));

export interface ListFieldRendererProps {
  fieldKey: string;
  fieldName: string;
  control: any;
  label?: string;
  itemFields?: Array<{
    key: string;
    label?: string;
    type?: 'text' | 'number' | 'currency' | 'percent' | 'date' | 'select';
  }>;
  disabled?: boolean;
  error?: string;
}

export const ListFieldRenderer: React.FC<ListFieldRendererProps> = ({
  fieldKey,
  fieldName,
  control,
  label,
  itemFields = [],
  disabled = false,
  error,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('accounting');

  // Stable identity store: assigns each unknown object exactly once via its JS-reference identity.
  // Survives shallow-copy mutations ([...items], { ...item }) because refs survive re-renders.
  // Identity map keyed by JS-object reference; assigned once per unique object.
  const itemIdentity = useRef(new Map<object, number>());
  const nextId = useRef(1);

  const displayLabel = label || t(`fields:${fieldKey}`, { defaultValue: String(fieldKey) });

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field }) => {
        const items = Array.isArray(field.value) ? field.value : [];

        const handleAddItem = () => {
          const newItem: any = {};
          itemFields.forEach((f) => {
            newItem[f.key] = f.type === 'number' || f.type === 'currency' || f.type === 'percent' ? 0 : '';
          });
          field.onChange([...items, newItem] as any);
        };

        const handleRemoveItem = (index: number) => {
          field.onChange(items.filter((_, i) => i !== index) as any);
        };

        const handleItemFieldChange = (index: number, itemFieldKey: string, value: any) => {
          // Mutate in-place so the object reference (and thus its identity key) survives edits.
          const updated = [...items];
          Object.assign(updated[index], { [itemFieldKey]: value });
          field.onChange(updated as any);
        };

        const getItemDisplayValue = (item: any): string => {
          if (item.name) return item.name;
          if (item.description) return item.description;
          return `Item ${items.indexOf(item) + 1}`;
        };

        const getItemSummary = (item: any): string => {
          const parts: string[] = [];
          if (item.amount !== undefined && item.amount !== null) {
            parts.push(formatCurrency(item.amount));
          }
          if (item.percent !== undefined && item.percent !== null) {
            parts.push(formatPercent(item.percent, 2));
          }
          return parts.length > 0 ? parts.join(' | ') : '';
        };

        return (
          <Box className={classes.listContainer}>
            <Box className={classes.listHeader}>
              <Typography className={classes.headerTitle}>
                {displayLabel}
              </Typography>
              <Button
                size="small"
                color="primary"
                onClick={handleAddItem}
                disabled={disabled}
                className={classes.addButton}
              >
                {t('common:add', { defaultValue: 'Add' })}
              </Button>
            </Box>

            {items.length === 0 ? (
              <Box className={classes.emptyState}>
                {t('common:no-items', { defaultValue: 'No items' })}
              </Box>
            ) : (
              <List disablePadding>
                {items.map((item, index) => {
                  // Prefer persisted ID (_id / id); assign stable generated IDs via ref-based identity map
                  // so editing a field never regenerates the key and remounts inputs.
                  const itemKey = (() => {
                    if (typeof item !== 'object' || item == null) return `primitive-${JSON.stringify(item)}`;
                    if (item._id) return String(item._id);
                    if (item.id) return String(item.id);
                    if (!itemIdentity.current.has(item)) {
                      itemIdentity.current.set(item, nextId.current);
                      nextId.current += 1;
                    }
                    return `obj-${itemIdentity.current.get(item)}`;
                  })();
                  return (
                    <ListItem key={itemKey} className={classes.listItem} disableGutters>
                      <Box style={{ width: '100%' }}>
                        <Box className={classes.itemHeader}>
                          <Box>
                            <Typography className={classes.itemTitle}>
                              {getItemDisplayValue(item)}
                            </Typography>
                            {getItemSummary(item) && (
                              <Typography variant="caption" color="textSecondary">
                                {getItemSummary(item)}
                              </Typography>
                            )}
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveItem(index)}
                            disabled={disabled}
                            className={classes.deleteButton}
                          >
                            ✕
                          </IconButton>
                        </Box>

                        {itemFields.length > 0 && (
                          <Box className={classes.itemFields}>
                            {itemFields.map((itemField) => (
                              <Box key={`${itemKey}-${itemField.key}`}>
                                <FieldRenderer
                                  fieldKey={itemField.key as any}
                                  fieldName={`${fieldName}.${index}.${itemField.key}`}
                                  control={control}
                                  label={itemField.label || t(`fields:${itemField.key}`, { defaultValue: itemField.key })}
                                  type={itemField.type || 'text'}
                                  disabled={disabled}
                                />
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
            )}

            {error && (
              <Typography style={{ fontSize: '0.75rem', color: '#d32f2f', marginTop: '0.5rem' }}>
                {error}
              </Typography>
            )}
          </Box>
        );
      }}
    />
  );
};

export default ListFieldRenderer;
